import type { Turn } from '@chessmate/protocol';

/**
 * Backgammon rules engine — pure functions over an explicit position type.
 *
 * This module deliberately does **not** implement `GameAdapter` and is not
 * registered in `adapters.ts`. Backgammon's state does not fit the chess-shaped
 * integration surface: there is no FEN for dice, a bar, and borne-off checkers,
 * and `PositionSnapshot` has nowhere to put them. Forcing it through that
 * surface would mean bending `packages/protocol` — which is frozen
 * additive-only and is exactly what this PR must not touch. So the engine
 * speaks its own position type, and the adapter/board/UI wiring is a later
 * slice with its own design decisions. Until then, the compiler's inability to
 * mention this module from the app is the guarantee that the surface stayed
 * untouched.
 *
 * Conventions, chosen to match how the game is *written* rather than how an
 * array is indexed:
 * - Points are **1-based** in the public API (point 1 … point 24), matching
 *   every backgammon book a parent might read alongside their kid.
 * - **White moves from high points to low** and owns home board 1–6; black
 *   moves low→high and owns 19–24. White bears off *past point 1*, black past
 *   point 24.
 * - A point number is encoded as the sign of the count at that index: white
 *   checkers positive, black negative. This is the one internal detail that
 *   leaks into the public shape (`BackgammonPosition.points`); everything else
 *   goes through the helpers below.
 *
 * The rules implemented are the standard ones: hitting blots, the forced bar
 * entry, the maximal-play obligation (play both dice if any sequence does; on
 * a non-double where only one die can play, the higher one must), and the
 * bearing-off rules including the higher-die-from-the-highest-point case.
 * Gammon and backgammon are detected because a kids' coach will want to say
 * "you won doubly!" rather than just "you won".
 */

/** A checker count per point, index 0 = point 1. Positive = white, negative = black. */
export type BackgammonPoints = readonly number[];

export interface BackgammonPosition {
  readonly points: BackgammonPoints;
  /** Checkers waiting to re-enter. White re-enters in 19–24, black in 1–6. */
  readonly bar: { readonly w: number; readonly b: number };
  /** Checkers successfully borne off. */
  readonly off: { readonly w: number; readonly b: number };
  readonly turn: Turn;
}

/** Where a step starts or ends. `'bar'` / `'off'`, or a 1-based point (1–24). */
export type BackgammonEndpoint = 'bar' | 'off' | number;

/** One die spent by one checker. `pips` is the die value, not the distance
 *  sign — direction is a property of the mover's colour. */
export interface BackgammonStep {
  readonly from: BackgammonEndpoint;
  readonly to: BackgammonEndpoint;
  readonly pips: number;
}

/** One complete dice play: up to two steps (four on doubles), in play order. */
export interface BackgammonTurn {
  readonly steps: readonly BackgammonStep[];
}

export type BackgammonDice = readonly [number, number];

/** The standard start: 2 on the 24-point, 5 on the 13, 3 on the 8, 5 on the 6 —
 *  black mirrored. White to move first is arbitrary (real backgammon rolls for
 *  it); callers decide the opening turn. */
export const BACKGAMMON_CHECKERS_PER_SIDE = 15;

export function backgammonStartPosition(turn: Turn): BackgammonPosition {
  const points = new Array<number>(24).fill(0);
  // White (positive). Index = point − 1.
  points[23] = 2; // 24-point
  points[12] = 5; // 13-point
  points[7] = 3; // 8-point
  points[5] = 5; // 6-point
  // Black (negative), the mirror image.
  points[0] = -2; // 1-point
  points[11] = -5; // 12-point
  points[16] = -3; // 17-point
  points[18] = -5; // 19-point
  return { points, bar: { w: 0, b: 0 }, off: { w: 0, b: 0 }, turn };
}

function countAt(points: BackgammonPoints, point: number): number {
  return points[point - 1] ?? 0;
}

/** White's home board is 1–6; black's is 19–24. */
export function homeBoardOf(color: Turn): { low: number; high: number } {
  return color === 'w' ? { low: 1, high: 6 } : { low: 19, high: 24 };
}

function isPointOpen(points: BackgammonPoints, color: Turn, point: number): boolean {
  const c = countAt(points, point);
  if (c === 0) return true;
  // Own checkers (any number) or an enemy blot (exactly 1) are landable.
  if (color === 'w') return c >= -1;
  return c <= 1;
}

function hitsBlot(points: BackgammonPoints, color: Turn, point: number): boolean {
  const c = countAt(points, point);
  return color === 'w' ? c === -1 : c === 1;
}

/** Pips still to walk for `color`: point number for white (walking down to 0),
 *  25 − point for black (walking up to 25), bar counts as 25, off counts as 0.
 *  The coach-facing number — "you still have 127 to go" — and the gate for the
 *  bearing-off rules. */
export function pipCount(pos: BackgammonPosition, color: Turn): number {
  let pips = pos.bar[color] * 25;
  for (let point = 1; point <= 24; point++) {
    const c = countAt(pos.points, point);
    if (c === 0 || (color === 'w' ? c < 0 : c > 0)) continue;
    const n = Math.abs(c);
    pips += color === 'w' ? n * point : n * (25 - point);
  }
  return pips;
}

/** All of `color`'s checkers are in the home board (none on the bar, none
 *  outside 1–6 for white / 19–24 for black). Gate for bearing off. */
export function allHome(pos: BackgammonPosition, color: Turn): boolean {
  if (pos.bar[color] > 0) return false;
  const { low, high } = homeBoardOf(color);
  for (let point = 1; point <= 24; point++) {
    if (point >= low && point <= high) continue;
    const c = countAt(pos.points, point);
    if (color === 'w' ? c > 0 : c < 0) return false;
  }
  return true;
}

/** The furthest-back occupied home point for `color` (1-based), or 0 if the
 *  home board is empty. Only consulted when `allHome` is true. "Furthest back"
 *  means farthest from bearing off: white's highest home point (6), but
 *  black's LOWEST (19) — black walks up toward 25, so 24 is its nearest point.
 *  Scanning from `high` for both colours made black's "rearmost" its nearest
 *  checker and let it waste a big die leapfrogging checkers still far out. */
function rearmostHomePoint(pos: BackgammonPosition, color: Turn): number {
  const { low, high } = homeBoardOf(color);
  if (color === 'w') {
    for (let point = high; point >= low; point--) {
      if (countAt(pos.points, point) > 0) return point;
    }
  } else {
    for (let point = low; point <= high; point++) {
      if (countAt(pos.points, point) < 0) return point;
    }
  }
  return 0;
}

/** Distance of an endpoint from the mover's own bearing-off edge: the point
 *  number for white, 25 - point for black, the bar as 25 and "off" as 0. */
function moverFrame(color: Turn, endpoint: BackgammonEndpoint): number {
  if (endpoint === 'bar') return 25;
  if (endpoint === 'off') return 0;
  return color === 'w' ? endpoint : 25 - endpoint;
}

/**
 * A total, colour-blind order on turns, for breaking ties between turns a
 * picker scores equally. Steps are compared in the MOVER'S frame (distance
 * from their own bearing-off edge), lowest first, i.e. "bring the checker
 * nearest home first". Because the key mirrors with the position, black's pick
 * is exactly white's pick seen from the other side.
 *
 * Why it exists: pickers sort by score and used to leave ties in
 * `legalTurns` enumeration order, which walks points 1..24 — nearest-home
 * first for white but farthest-back first for black. Greedy scores tie on most
 * moves, so the robot played a different style per colour (40 of 40 seeded
 * Easy-vs-Easy games went to white, and Easy as black lost to a uniformly
 * random mover 80% of the time).
 */
export function compareTurnsInMoverFrame(
  color: Turn,
  a: BackgammonTurn,
  b: BackgammonTurn,
): number {
  const n = Math.min(a.steps.length, b.steps.length);
  for (let i = 0; i < n; i++) {
    const x = a.steps[i] as BackgammonStep;
    const y = b.steps[i] as BackgammonStep;
    const d =
      moverFrame(color, x.from) - moverFrame(color, y.from) ||
      moverFrame(color, x.to) - moverFrame(color, y.to) ||
      x.pips - y.pips;
    if (d !== 0) return d;
  }
  return a.steps.length - b.steps.length;
}

/** Apply one step. Assumes legality (checked by the enumeration); the job here
 *  is fidelity: pick up, drop, and send a hit blot to the bar. */
export function applyStep(
  pos: BackgammonPosition,
  color: Turn,
  step: BackgammonStep,
): BackgammonPosition {
  const points = [...pos.points];
  const bar = { ...pos.bar };
  const off = { ...pos.off };

  if (step.from === 'bar') bar[color] -= 1;
  else {
    const i = (step.from as number) - 1;
    const leaving = points[i] ?? 0;
    points[i] = color === 'w' ? leaving - 1 : leaving + 1;
  }

  if (step.to === 'off') {
    off[color] += 1;
  } else {
    const t = (step.to as number) - 1;
    if (hitsBlot(points, color, step.to as number)) {
      const enemy = color === 'w' ? 'b' : 'w';
      bar[enemy] += 1;
      points[t] = 0;
    }
    const landing = points[t] ?? 0;
    points[t] = color === 'w' ? landing + 1 : landing - 1;
  }

  return { points, bar, off, turn: color };
}

/** Landing point for `color` moving `pips` from `from`, or null when the walk
 *  runs off the wrong edge. White decrements, black increments. */
function landingPoint(color: Turn, from: number, pips: number): number | null {
  return color === 'w' ? from - pips : from + pips;
}

/** The point `color` re-enters on with `pips`, or null. White's entry window is
 *  the black home board (19–24) and vice versa — the far corner of the board. */
function entryPoint(color: Turn, pips: number): number {
  return color === 'w' ? 25 - pips : pips;
}

/** The single-step options for `color` spending one die of `pips` right now:
 *  bar entry (forced when checkers are on the bar), ordinary moves, and legal
 *  bearing off. Bearing off a checker on point `p` is legal when `p === pips`
 *  exactly, or when `pips` overshoots AND `p` is the rearmost occupied home
 *  point — you may waste a big die only on the furthest-back checker. */
export function stepsForDie(pos: BackgammonPosition, color: Turn, pips: number): BackgammonStep[] {
  const out: BackgammonStep[] = [];

  // The bar is a queue, not a choice: while it holds checkers, nothing else may move.
  if (pos.bar[color] > 0) {
    const point = entryPoint(color, pips);
    if (isPointOpen(pos.points, color, point)) {
      out.push({ from: 'bar', to: point, pips });
    }
    return out;
  }

  const { low, high } = homeBoardOf(color);
  const canBearOff = allHome(pos, color);
  const rearmost = canBearOff ? rearmostHomePoint(pos, color) : 0;

  for (let point = 1; point <= 24; point++) {
    const c = countAt(pos.points, point);
    if (c === 0 || (color === 'w' ? c < 0 : c > 0)) continue;

    const landing = landingPoint(color, point, pips);
    const inHomeRange =
      color === 'w' ? landing !== null && landing >= 1 : landing !== null && landing <= 24;
    if (landing !== null && inHomeRange && isPointOpen(pos.points, color, landing)) {
      out.push({ from: point, to: landing, pips });
    }

    if (canBearOff) {
      const offPoint = color === 'w' ? point : 25 - point;
      // Exact: point number equals the die (white walks 6→off with a 6).
      if (offPoint === pips) {
        out.push({ from: point, to: 'off', pips });
      } else if (offPoint < pips && point === rearmost) {
        // Overshoot: only from the rearmost occupied home point, so a big die
        // can never leapfrog a checker that still has further to travel.
        out.push({ from: point, to: 'off', pips });
      }
    }
  }

  return out;
}

/**
 * Every complete legal dice play for `pos.turn` given one roll.
 *
 * Enumeration walks every ordering of the dice; on doubles the die repeats four
 * times. The hard part is not generating sequences — it is **when to stop**:
 * backgammon obliges you to play the dice as fully as the position allows. So
 * this collects every maximal-length sequence and applies the tie-break: on a
 * non-double where the longest play is a single step, only sequences that spend
 * the **higher** die survive, if any do. (The higher-die rule is conventional,
 * not logical — both single steps leave the same pip count — which is exactly
 * the kind of rule a rules engine exists to encode rather than argue about.)
 */
export function legalTurns(pos: BackgammonPosition, dice: BackgammonDice): BackgammonTurn[] {
  const color = pos.turn;
  const [dieA, dieB] = dice;
  if (dieA === undefined || dieB === undefined || dieA < 1 || dieA > 6 || dieB < 1 || dieB > 6)
    return [];
  const a: number = dieA;
  const b: number = dieB;
  const remaining = a === b ? [a, a, a, a] : [a, b];

  const out: BackgammonTurn[] = [];

  const dfs = (current: BackgammonPosition, pool: number[], steps: BackgammonStep[]): void => {
    let extended = false;
    const tried = new Set<number>();
    for (let i = 0; i < pool.length; i++) {
      const pips = pool[i];
      if (pips === undefined) continue;
      if (tried.has(pips)) continue; // identical dice are interchangeable — don't re-walk
      tried.add(pips);
      for (const step of stepsForDie(current, color, pips)) {
        extended = true;
        const rest = pool.slice(0, i).concat(pool.slice(i + 1));
        dfs(applyStep(current, color, step), rest, [...steps, step]);
      }
    }
    if (!extended && steps.length > 0) {
      out.push({ steps: [...steps] });
    }
  };

  dfs(pos, remaining, []);

  if (out.length === 0) return out;

  // Maximal-play obligation: keep only the longest sequences.
  const maxLen = Math.max(...out.map((t) => t.steps.length));
  let kept = out.filter((t) => t.steps.length === maxLen);

  // Higher-die rule, non-doubles only: when even the highest die cannot be
  // paired with anything, the sole step must spend the larger die.
  if (maxLen === 1 && a !== b) {
    const high = Math.max(a, b);
    const usingHigh = kept.filter((t) => t.steps[0]?.pips === high);
    if (usingHigh.length > 0) kept = usingHigh;
  }

  return kept;
}

/** Apply a complete turn. Steps are validated by construction — this is only
 *  called with the output of `legalTurns` — so the function is a fold of
 *  `applyStep` plus the handover of turn. The input is never mutated. */
export function applyTurn(pos: BackgammonPosition, turn: BackgammonTurn): BackgammonPosition {
  let current = pos;
  for (const step of turn.steps) {
    current = applyStep(current, pos.turn, step);
  }
  const enemy = pos.turn === 'w' ? 'b' : 'w';
  return { points: current.points, bar: current.bar, off: current.off, turn: enemy };
}

/**
 * Result of a finished game, or null while it runs.
 *
 * A plain win is `elimination` — the checker-count kind, same `GameResult`
 * *shape* the other engines report, but produced locally: this module does not
 * import the protocol's `GameResult` on purpose. Its `kind` union is
 * chess-shaped ('checkmate' | 'resign' | …), and backgammon's real vocabulary
 * is win / gammon / backgammon. Rather than fork or bend the frozen protocol
 * type, this module declares its own result and the future adapter slice will
 * map it across at the seam.
 */
export type BackgammonResult =
  | { kind: 'elimination'; winner: Turn }
  | { kind: 'gammon'; winner: Turn }
  | { kind: 'backgammon'; winner: Turn };

export function backgammonResultOf(pos: BackgammonPosition): BackgammonResult | null {
  for (const winner of ['w', 'b'] as const) {
    if (pos.off[winner] < BACKGAMMON_CHECKERS_PER_SIDE) continue;
    const loser = winner === 'w' ? 'b' : 'w';
    // Gammon: the loser bore off nothing. Backgammon: worse — the loser still
    // has a checker in the winner's home board or on the bar when the game ended.
    if (pos.off[loser] === 0) {
      const { low, high } = homeBoardOf(winner);
      let stranded = pos.bar[loser] > 0;
      for (let point = low; point <= high && !stranded; point++) {
        const c = countAt(pos.points, point);
        stranded = loser === 'w' ? c > 0 : c < 0;
      }
      return stranded ? { kind: 'backgammon', winner } : { kind: 'gammon', winner };
    }
    return { kind: 'elimination', winner };
  }
  return null;
}

/**
 * The tournament opening: each side rolls one die; the higher die plays
 * first, using BOTH dice as its opening roll. A tie is no decision — the
 * caller re-rolls (null here). Never produces doubles, so the opening turn
 * is always an ordinary two-die play.
 */
export function openingTurnOf(d1: number, d2: number): Turn | null {
  if (d1 === d2) return null;
  return d1 > d2 ? 'w' : 'b';
}
